import { forwardRef } from 'react';
import { useEventTypesQuery } from '../../lib/Queries';
import CheckboxSelector from '../CheckboxSelector';
import TStrings from './Select.i18n';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '~/lib/useBusinessNavigate';

interface CompactEventTypeSelectProps {
    value?: Array<string>; // Array of selected event type IDs
    id: string; // ID for the EventTypeCheckboxes component
    initialOpen?: boolean; // Whether the dropdown is initially open
    excludeClasses?: boolean; // Whether to exclude class event types from this list
    disableClasses?: boolean; // Whether to show class event types as disabled
    onlyClasses?: boolean;
    onChange: (selected: Array<unknown>) => void; // Callback function for when the selection changes
    defaultAll?: boolean; // Whether to select all by default
    className?: string; // Additional class names for styling
    containerClassName?: string;
    maxDisplay?: number; // the max number of children to display
}
export type CompactEventTypeImperativeHandle = {
    close: () => void; // Method to close the dropdown
};

const CompactEventTypeSelect = forwardRef<
    CompactEventTypeImperativeHandle,
    CompactEventTypeSelectProps
>(
    (
        {
            value: selectedIds,
            id,
            initialOpen,
            excludeClasses,
            disableClasses,
            onlyClasses,
            onChange,
            defaultAll,
            className,
            maxDisplay = 2,
            containerClassName,
        },
        ref,
    ) => {
        const { data } = useEventTypesQuery();
        const eventTypeOptions = (data || [])
            .map((type: { is_class: any; name: string; id: string }) => {
                if (!type) {
                    return null;
                }

                if (excludeClasses && type.is_class) {
                    return null;
                }

                if (onlyClasses && !type.is_class) {
                    return null;
                }

                let disabled = {};
                if (disableClasses) {
                    disabled = {
                        disabled: !!type.is_class,
                        subtext: type.is_class ? (
                            <span>
                                Change class availability in{' '}
                                <Link
                                    className="text-gray-400"
                                    to={getBusinessUrl(
                                        `/scheduling/event-types/${type.id}`,
                                    )}
                                >
                                    Event Types
                                </Link>
                            </span>
                        ) : (
                            ''
                        ),
                    };
                }

                return {
                    label: type.name,
                    value: type.id,
                    id: type.id,
                    group: type?.category?.name,
                    ...disabled,
                };
            })
            .filter(Boolean);

        return (
            <CheckboxSelector
                ref={ref}
                initialOpen={initialOpen}
                id={id}
                onChange={onChange}
                allValues={eventTypeOptions}
                selectedValues={selectedIds}
                defaultAll={defaultAll}
                className={className}
                maxDisplay={maxDisplay}
                containerClassName={containerClassName}
                itemNamePlural={TStrings.ITEM_NAME_PLURAL_DESCRIPTOR}
            />
        );
    },
);

export default CompactEventTypeSelect;
